import {
  Alert,
  Box,
  Button,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import useSummarize from "features/Bookmarks/hooks/useSummarizer.hook";

import { convertFromHTML, ContentState, EditorState } from "draft-js";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Header from "./Header";
import SummaryBox from "./Box";

import { marked } from "marked";
import Summarizer from "features/Summarizer";
import { useSourceSearch, useTrackedArticles } from "features/SourceSearch";
import SearchResultLink from "features/SourceSearch/components/SearchResultLink";
import SummarizerErrorBox from "features/Summarizer/components/summary-error-box";

const Summarize = ({
  markdownSummary,
  setmarkdownSummary,
  summaryLoaded,
  setSummaryLoaded,
  selectedBookmarkOption,
  edit,
  setDisable,
  originalSummary,
  setOriginalSummary,
  setIsAIGeneratedContent,
  isAIGeneratedContent
}) => {
  const [htmlSummary, sethtmlSummary] = useState(
    EditorState.createEmpty(markdownToContentState(markdownSummary))
  );

  const [editing, setEditing] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [loading, setLoading] = useState(true);

  const { isSummaryAvailable, summaryInfoData } = useSelector(
    (state) => state.data
  );

  const { hasBeenClicked, updateTrackedLink } = useTrackedArticles(
    summaryInfoData?.url
  );

  const { currentCategory, currentSearchTerm } = useSourceSearch();

  const handleInputChange = (e, change) => {
    change(e.target.value);
  };

  const {
    handleCopy,
    changeTooltip,
    handleMouseOut,
    error,
    errorMessage,
    setError,
  } = useSummarize(
    htmlSummary,
    sethtmlSummary,
    markdownSummary,
    setmarkdownSummary,
    setLoading,
    summaryLoaded,
    setSummaryLoaded,
    selectedBookmarkOption,
    edit,
    setDisable,
    setOriginalSummary,
    setIsAIGeneratedContent
  );

  const theme = useTheme();
  const navbarBackground = theme.palette?.navbar?.main;

  const tooltipTitle = changeTooltip
    ? "Copied to clipboard"
    : "Copy to clipboard";

  const saveSummary = (markdown) => {
    setmarkdownSummary(markdown);
    setIsFocused(false);
  };

  function markdownToHtml(markdown) {
    return marked(markdown);
  }

  function markdownToContentState(markdown) {
    const html = markdownToHtml(markdown);
    const contentState = ContentState.createFromBlockArray(
      convertFromHTML(html).contentBlocks
    );
    return EditorState.createWithContent(contentState);
  }

  useEffect(() => {
    if (originalSummary && originalSummary.length > 1) {
      if (markdownSummary === originalSummary) {
        setDisable(true);
      } else {
        setDisable(false);
      }
    }
    if (!editing) {
      sethtmlSummary(markdownToContentState(markdownSummary));
    }
  }, [markdownSummary, edit]);
  

  // useEffect(() => {
  //   if (markdownSummary.length > 20) {
  //     setError(false);
  //   }
  // }, [markdownSummary]);

  useEffect(() => {
    if(error) {
      setIsAIGeneratedContent(false);
    }
  }, [error]);

  useEffect(() => {
    updateTrackedLink(summaryInfoData?.url);
  }, [summaryInfoData, updateTrackedLink]);

  if (selectedBookmarkOption !== "summary") {
    return null;
  }

  if (!isSummaryAvailable && !hasBeenClicked) {
    return (
      <Stack gap={2} p={2} pt={4}>
        <Alert severity="info">
          <Typography>
            It looks like you haven’t opened this article yet. Before using our
            generative AI tools, please open and read the article first. This
            ensures you’re familiar with the original content in case you want
            to edit the AI-generated text. It is also respectful to the human
            writers who worked hard to create thoughtful, credible content for
            you.
          </Typography>
        </Alert>
        <Stack direction="row" justifyContent="center">
          <SearchResultLink
            url={summaryInfoData.url}
            trackingCat="click_resultset_title"
            trackingOptions={{
              date_published: summaryInfoData.utc_date_published,
              url: summaryInfoData.url,
              query:
                currentSearchTerm != null &&
                currentSearchTerm.trim().length > 0
                  ? currentSearchTerm
                  : undefined,
              entity: summaryInfoData.publisher,
              category:
                currentCategory != null && currentCategory.trim().length > 0
                  ? currentCategory
                  : undefined,
              type: "bookmark sidebar",
              title: summaryInfoData.title,
            }}
            component={Button}
            sx={{
              color: theme.palette.primary.contrastText,
            }}
          >
            Open Article
          </SearchResultLink>
        </Stack>
      </Stack>
    );
  }
  if (loading) {
    return (
      <Stack direction="column" gap={2} p={2} pt={4}>
        <Skeleton variant="rectangular" height={50} />
        <Skeleton variant="rectangular" height={150} />
        <Skeleton variant="rectangular" height={50} />
      </Stack>
    );
  }
  return (
    <Box>
      {error ? (
        <Box
          sx={{
            px: 2,
          }}
        >
          <SummarizerErrorBox
            errorMessage={errorMessage}
            markdownSummary={markdownSummary}
            setmarkdownSummary={setmarkdownSummary}
            setDisable={setDisable}
            editing={editing}
            setEditing={setEditing}
            isFocused={isFocused}
            setIsFocused={setIsFocused}
            saveSummary={saveSummary}
            htmlSummary={htmlSummary}
            sethtmlSummary={sethtmlSummary}
          />
        </Box>
      ) : (
        <Stack p={2} gap={2}>
          {isAIGeneratedContent && (
            <React.Fragment>
              <Header />
              <Typography sx={{ mt: 2 }} variant="body2" color="grey">
                Generated Summary
              </Typography>
            </React.Fragment>
          )}
          <SummaryBox
            editing={editing}
            setEditing={setEditing}
            markdownSummary={markdownSummary}
            setmarkdownSummary={setmarkdownSummary}
            htmlSummary={htmlSummary}
            sethtmlSummary={sethtmlSummary}
            saveSummary={saveSummary}
            isFocused={isFocused}
            setIsFocused={setIsFocused}
          />

          {!editing && (
            <Stack direction="row" gap={2}>
              <Button
                startIcon={
                  <EditOutlinedIcon
                  sx={{ color: theme.palette.secondary.main }}
                  />
                }
                sx={{
                  mt: "2rem",
                  px: 2,
                  py: 1,
                  color: "black",
                  backgroundColor: navbarBackground,
                  textTransform: "none",
                }}
                onClick={() => {
                  setEditing((edit) => !edit);
                }}
              >
                Edit
              </Button>

              <Tooltip title={tooltipTitle}>
                <Button
                  startIcon={
                    <ContentCopyIcon
                    sx={{ color: theme.palette.secondary.main }}
                    />
                  }
                  sx={{
                    mt: "2rem",
                    px: 2,
                    py: 1,
                    color: "black",
                    backgroundColor: navbarBackground,
                    textTransform: "none",
                  }}
                  onClick={handleCopy}
                  onMouseOut={handleMouseOut}
                >
                  Copy
                </Button>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default Summarize;
